import defaultProfile, { asyncRoute } from "./default";
import { type ConfigSection, type GameProfile, type Menu, MenuItem, type ProfileCtx } from "./types";
import { i18n } from "@/i18n";
import { faKey, faWrench } from "@fortawesome/free-solid-svg-icons";

const { t } = i18n.global;

const createMenu = (ctx: ProfileCtx): Menu => {
  const settingsMenuSection = {
    name: "settings",
    items: [
      new MenuItem(
        "",
        faWrench,
        asyncRoute(() => import("../components/views/config/Conf.vue")),
      ),
    ],
  };

  const permissionsMenuSection = {
    name: "administration",
    items: [
      new MenuItem(
        "permissions",
        faKey,
        asyncRoute(() => import("../components/views/Permissions.vue")),
      ),
    ],
  };

  return [settingsMenuSection, permissionsMenuSection, ...defaultProfile(ctx).menu.slice(2)];
};

// This profile replaces the default auto-built section list, so every section in
// games_games.form_configuration_new must be listed here or it never renders.
//
// Listing a section costs nothing on a row that does not have it — ConfSection renders nothing
// for a missing section — which is why TR-5418 could move the Server Custom Settings entries here
// while the PC game rows still lack them.
export const baseConfigSections: ConfigSection[] = [
  { component: "Battlegroup" },
  { component: "ConfSection", props: { name: "basic" } },
  // TR-5375: sandworm threat detection (UserGame.ini -> SandwormSettings).
  { component: "ConfSection", props: { name: "sandworm" } },
  // TR-5375 / TR-5418: the Server Custom Settings (UserServerCustomSettings.ini). These were on
  // the console profile while the delivery was validated on PS5; TR-5418 moved them here along
  // with `configs` on DuneAwakening and "dune" in gpd's SERVER_CUSTOM_SETTINGS_SERVICE_TYPES.
  { component: "ConfSection", props: { name: "harvesting" } },
  { component: "ConfSection", props: { name: "combat_xp" } },
  { component: "ConfSection", props: { name: "survival" } },
  { component: "ConfSection", props: { name: "landsraad" } },
  // TR-5192 / TR-5375: `building` carries both deliveries — the UserGame.ini building-restriction
  // keys and three Server Custom Settings. One entry, listed once: a section named twice renders
  // twice.
  { component: "ConfSection", props: { name: "building" } },
];

const join = () => {
  window.open(t("ngp/settings/section/dune_wiki/link"), "_blank", "noopener,noreferrer");
};

export default (_ctx: ProfileCtx): Partial<GameProfile> => ({
  menu: createMenu(_ctx),
  configSections: baseConfigSections,
  hideHeaderIpPort: true,
  hasActivePlayers: false,
  hasBattlegroups: true,
  join: {
    buttonTextKey: "ngp/settings/section/dune_wiki",
    joinFn: join,
  },
});
